import React from 'react';
import {
    IStructuredMessageSection,
    IStructuredProgressSection,
    IStructuredTableRow,
    IStructuredTableSection
} from 'services/interface';
import './StructuredMessage.scss';

interface StructuredMessageProps {
    sections: IStructuredMessageSection[];
}

const clampPercent = (value: number) => Math.min(100, Math.max(0, value));

const getCellValue = (cell: IStructuredTableRow['cells'][number]) =>
    typeof cell === 'object' && cell !== null ? cell.value : cell;

const getCellColor = (cell: IStructuredTableRow['cells'][number]) =>
    typeof cell === 'object' && cell !== null ? cell.color : undefined;

const getRowKey = (row: IStructuredTableRow) =>
    row.cells.map(cell => getCellValue(cell)).join('-');

const getProgressLabel = (item: any) => item.label || item.name || item.appName || '';

const getProgressValue = (item: any) => item.value || item.amount || item.displayAmount || '';

const getProgressFillColor = (item: any) => item.color;


const getProgressPercent = (item: any) => clampPercent(Number(item.percent ?? item.progress ?? item.ratio ?? 0));

const renderTableSection = (section: IStructuredTableSection, index: number) => (
    <article className="structured-message__card" key={`${section.type}-${index}`}>
        <h3 className="structured-message__title">{section.title}</h3>

        <div className="structured-message__table">
            <div className="structured-message__table-header">
                {section.headers.map((header, headerIndex) => (
                    <span key={`${header}-${headerIndex}`} className="structured-message__table-cell structured-message__table-cell--header">
                        {header}
                    </span>
                ))}
            </div>

            <div className="structured-message__table-body">
                {section.rows.map((row, rowIndex) => (
                    <div key={`${rowIndex}-${getRowKey(row)}`} className="structured-message__table-row">
                        {row.cells.map((cell, cellIndex) => (
                            <span
                                key={`${rowIndex}-${cellIndex}`}
                                className={`structured-message__table-cell ${row.highlightCellIndexes?.includes(cellIndex) ? 'structured-message__table-cell--highlight' : ''}`}
                                style={getCellColor(cell) ? { color: getCellColor(cell) } : undefined}
                            >
                                {getCellValue(cell)}
                            </span>
                        ))}
                    </div>
                ))}
            </div>
        </div>

        {section.summary && (
            <div className="structured-message__summary">
                {section.summary.label}{' '}
                <strong>{section.summary.value}</strong>{' '}
                {section.summary.suffix && <span>{section.summary.suffix}</span>}
            </div>
        )}
    </article>
);

const renderProgressSection = (section: IStructuredProgressSection, index: number) => (
    <article className="structured-message__card" key={`${section.type}-${index}`}>
        <h3 className="structured-message__title">{section.title}</h3>

        <div className="structured-message__progress-list">
            {section.items.map((item, itemIndex) => {
                const percent = getProgressPercent(item);
                const label = getProgressLabel(item);
                const value = getProgressValue(item);

                return (
                    <div className="structured-message__progress-item" key={`${label}-${itemIndex}`}>
                        <div className="structured-message__progress-meta">
                            <span className="structured-message__progress-label">{label}</span>
                            <span
                                className="structured-message__progress-value"
                            >
                                {value}
                            </span>
                        </div>

                        <div
                            className="structured-message__progress-track"
                            role="progressbar"
                            aria-label={`${label} usage`}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={percent}
                        >
                            <div
                                className="structured-message__progress-fill"
                                style={{
                                    width: `${percent}%`,
                                    backgroundColor: getProgressFillColor(item)
                                }}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    </article>
);

const StructuredMessage: React.FC<StructuredMessageProps> = ({ sections }) => {
    if (!sections.length) {
        return null;
    }

    return (
        <div className="structured-message">
            {sections.map((section, index) => (
                section.type === 'table'
                    ? renderTableSection(section, index)
                    : renderProgressSection(section as IStructuredProgressSection, index)
            ))}
        </div>
    );
};

export default StructuredMessage;
